import { Injectable } from '@angular/core';
import { pdfPageWidthOnLandscape } from '@core/helpers';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '@shared/interfaces';
import type { RowInput } from 'jspdf-autotable';

@Injectable({
    providedIn: 'root',
})
export class ExportService {
    constructor(private translateService: TranslateService) {}

    async exportPdf(columnNames: string[], body: RowInput[], columnStyles: any, pdfTitle: string, pdfName: string) {
        const jsPDF = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF.default('landscape');
        doc.text(pdfTitle, 8, 16);

        autoTable(doc, {
            margin: { top: 25, left: 8 },
            head: [columnNames],
            body: body,
            columnStyles: columnStyles,
            didDrawPage: data => {
                data.settings.margin.top = 10;
            },
        });

        doc.save(`${pdfName}.pdf`);
    }

    async exportExcel(rows: any[], columnStyles: any[], fileName: string): Promise<void> {
        const XLSX = await import('xlsx-js-style');
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([...rows]);
        ws['!cols'] = columnStyles;
        XLSX.utils.book_append_sheet(wb, ws, fileName);
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    }

    getColumnWidthOnPdf(columns: TableColumn<any>[]) {
        let columnStyles: any = {};

        const totalRatio = this.getTotalRatio(columns);
        const mediumRatio = pdfPageWidthOnLandscape / totalRatio;

        columns.forEach((column: TableColumn<any>, index: number) => {
            columnStyles[index] = { cellWidth: mediumRatio * column.widthRatio };
        });

        return columnStyles;
    }

    getColumnNames(visibleColumns: TableColumn<any>[], columnsNameText: Record<any, string>): string[] {
        let columnNames: string[] = [];
        visibleColumns.map((column: TableColumn<any>) => {
            columnNames.push(this.translateService.instant(columnsNameText[column.field]));
        });

        return columnNames;
    }

    mapListToExportExcel(columns: string[], rowData: any[]) {
        const headerRow: any[] = [];
        columns.forEach(col => {
            headerRow.push({
                v: col,
                t: 's',
                s: { font: { bold: true, color: { rgb: 'ffffff' } }, fill: { fgColor: { rgb: '2980BA' } } },
            });
        });

        const rows: any[] = rowData;

        return [headerRow, ...rows];
    }

    getColumnWidthOnExcel(visibleColumns: TableColumn<any>[]) {
        let columnStyles: any[] = [];
        const mediumRatio = 10;

        visibleColumns.forEach((column: TableColumn<any>) => {
            columnStyles.push({ wch: mediumRatio * column.widthRatio });
        });

        return columnStyles;
    }

    insertRow(data: any[]): any[] {
        const result: any[] = [];

        data.forEach((item: any) => {
            let row: any[] = [];
            Object.keys(item).forEach(key => {
                row.push({
                    v: item[key],
                    t: 's',
                });
            });
            result.push(row);
        });

        return result;
    }

    private getTotalRatio(columns: TableColumn<any>[]): number {
        let totalRatio = 0;
        columns.forEach(col => {
            totalRatio += col.widthRatio;
        });

        return totalRatio;
    }
}
